import React, { useMemo, useState } from 'react';
import { Search, ArrowRight, Sparkles } from 'lucide-react';
import * as Icons from 'lucide-react';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import CtaBanner from '../components/CtaBanner';
import Seo from '../components/Seo';
import { ALL_FEATURES, HEADLINE_FEATURES } from '../data/mock';

/**
 * Phone Features page.
 *
 * Two sections:
 *   1) "Headline features" — 9 differentiated, sales-grade capabilities
 *      rendered as rich cards with a description. These are the things
 *      that win demos (Sentiment Analysis, Call Trace, 240+ APIs, etc.).
 *   2) Filterable + searchable grid of every feature the platform exposes
 *      (50+ entries). Each card now carries a one-line description so the
 *      page is genuinely useful for buyers comparing on capability.
 *
 * Data lives in src/data/mock.js — both HEADLINE_FEATURES and the
 * enriched ALL_FEATURES array.
 */
const CATEGORIES = ['All', 'Calling', 'Routing', 'Productivity', 'Collaboration', 'Analytics', 'Admin', 'Mobility'];

// Accent colour map for the headline cards.
const ACCENT = {
  violet: 'from-violet-50 to-white border-violet-100 text-violet-700',
  fuchsia: 'from-fuchsia-50 to-white border-fuchsia-100 text-fuchsia-700',
  rose: 'from-rose-50 to-white border-rose-100 text-rose-700',
  sky: 'from-sky-50 to-white border-sky-100 text-sky-700',
  indigo: 'from-indigo-50 to-white border-indigo-100 text-indigo-700',
  amber: 'from-amber-50 to-white border-amber-100 text-amber-700',
  emerald: 'from-emerald-50 to-white border-emerald-100 text-emerald-700',
  teal: 'from-teal-50 to-white border-teal-100 text-teal-700',
  slate: 'from-slate-50 to-white border-slate-200 text-slate-700',
};
const ACCENT_ICON = {
  violet: 'bg-violet-500',
  fuchsia: 'bg-fuchsia-500',
  rose: 'bg-rose-500',
  sky: 'bg-sky-500',
  indigo: 'bg-indigo-500',
  amber: 'bg-amber-500',
  emerald: 'bg-emerald-500',
  teal: 'bg-teal-500',
  slate: 'bg-slate-700',
};

export default function PhoneFeatures() {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('All');

  const filtered = useMemo(() => {
    return ALL_FEATURES.filter((f) => {
      const matchCat = cat === 'All' || f.cat === cat;
      const ql = q.toLowerCase();
      const matchQ = !q
        || f.name.toLowerCase().includes(ql)
        || (f.blurb || '').toLowerCase().includes(ql);
      return matchCat && matchQ;
    });
  }, [q, cat]);

  return (
    <>
      <Seo
        title={`${ALL_FEATURES.length}+ Phone System Features | VoIPinger`}
        description="From AI-driven IVR and sentiment-analysed recordings to drag-drop wallboards and 240+ open APIs — every capability a modern business phone system can offer."
        path="/phone-features"
        image="https://voipinger.com/assets/heroes/phone-features.png"
        service={{ name: 'Phone System Features', serviceType: 'Business phone system features' }}
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Phone Features', path: '/phone-features' }]}
      />
      <PageHero
        eyebrow="Phone System Features"
        title={`${ALL_FEATURES.length}+ features. Zero compromises.`}
        subtitle="From AI-driven IVR and sentiment-analysed recordings to drag-drop wallboards and 240+ open APIs — every capability a modern business phone system can offer, in plain English."
        breadcrumb="Phone Features"
      />

      {/* HEADLINE / SPOTLIGHT FEATURES */}
      <section className="bg-gradient-to-b from-white via-violet-50/30 to-white">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="eyebrow"><Sparkles size={11} /> Differentiated capabilities</span>
            <h2 className="section-title mt-4">Features that win demos</h2>
            <p className="section-sub mt-3">
              The capabilities most customers haven't seen anywhere else — AI transcription,
              sentiment analysis, full call-trace forensics and an open API platform.
            </p>
            <div className="divider-svg mt-6" />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {HEADLINE_FEATURES.map((f) => {
              const Ic = Icons[f.icon] || Icons.Sparkles;
              const cardCls = ACCENT[f.accent] || ACCENT.violet;
              const iconCls = ACCENT_ICON[f.accent] || ACCENT_ICON.violet;
              return (
                <div
                  key={f.name}
                  className={`relative rounded-2xl border bg-gradient-to-br ${cardCls} p-6 flex flex-col hover:-translate-y-1 hover:shadow-xl transition-all`}
                >
                  {f.tag && (
                    <span className="absolute top-4 right-4 inline-flex px-2 py-0.5 rounded-full bg-white/90 text-[10px] font-extrabold uppercase tracking-wider text-slate-700 border border-slate-200">
                      {f.tag}
                    </span>
                  )}
                  <div className={`w-12 h-12 rounded-xl ${iconCls} text-white flex items-center justify-center mb-4 shadow-lg shadow-slate-900/10`}>
                    <Ic size={22} />
                  </div>
                  <h3 className="font-extrabold text-slate-900 text-lg leading-tight mb-2">{f.name}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.blurb}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link to="/contact" className="btn-primary">
              See it in a 20-minute demo <ArrowRight size={14} />
            </Link>
            <a href="#all-features" className="btn-ghost">
              Browse every feature
            </a>
          </div>
        </div>
      </section>

      {/* FULL FILTERABLE FEATURE GRID */}
      <section id="all-features" className="bg-white">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <span className="eyebrow">Complete feature list</span>
            <h2 className="section-title mt-4">Filter or search — find exactly what you need</h2>
            <p className="section-sub mt-3">
              Every standard feature included on every seat. Toggle a category, or type a keyword
              like "queue", "API" or "Teams" to jump straight to the capability you're checking.
            </p>
          </div>

          <div className="max-w-2xl mx-auto mb-6 relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder='Search a feature... (e.g. "IVR", "recording", "Teams")'
              className="w-full pl-11 pr-4 py-3.5 rounded-full border border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 outline-none"
            />
          </div>

          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {CATEGORIES.map((c) => {
              const active = cat === c;
              const count = c === 'All' ? ALL_FEATURES.length : ALL_FEATURES.filter((f) => f.cat === c).length;
              return (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                    active
                      ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-lg shadow-violet-500/30'
                      : 'bg-white text-slate-700 border border-slate-200 hover:border-violet-300'
                  }`}
                >
                  {c} <span className={`ml-1 text-xs ${active ? 'text-violet-100/90' : 'text-slate-400'}`}>({count})</span>
                </button>
              );
            })}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((f, i) => {
              const Ic = Icons[f.icon] || Icons.Sparkles;
              return (
                <div
                  key={`${f.name}-${i}`}
                  className="group rounded-2xl bg-white border border-slate-200 p-5 hover:border-violet-300 hover:shadow-md hover:-translate-y-0.5 transition-all"
                >
                  <div className="flex items-start gap-3 mb-2">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center shadow-md shadow-violet-500/20 flex-shrink-0">
                      <Ic size={18} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-slate-900 leading-tight">{f.name}</p>
                      <span className="text-[10px] uppercase tracking-wider text-violet-600 font-semibold">{f.cat}</span>
                    </div>
                  </div>
                  {f.blurb && (
                    <p className="text-sm text-slate-600 leading-relaxed">{f.blurb}</p>
                  )}
                </div>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <p className="text-center text-slate-500 mt-10">
              No feature matches your search. Try a different keyword or clear the filter.
            </p>
          )}
        </div>
      </section>

      {/* PLATFORM TRUST STRIP */}
      <section className="bg-slate-50 border-y border-slate-100">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <span className="eyebrow">Why this matters</span>
            <h2 className="section-title mt-4">Every feature, on a platform that doesn't fall over</h2>
            <p className="section-sub mt-3">
              These aren't just bullet points — they run on a globally distributed UC platform with
              three Tier-4 datacentres and 99.99% uptime.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5 max-w-4xl mx-auto">
            {[
              { v: '99.99%', l: 'Platform uptime' },
              { v: '3', l: 'Tier-4 datacentres (LON / AMS / LAS)' },
              { v: '240+', l: 'Open APIs' },
            ].map((s) => (
              <div key={s.l} className="rounded-2xl bg-white border border-slate-200 p-5 text-center">
                <p className="text-3xl md:text-4xl font-extrabold gradient-text">{s.v}</p>
                <p className="text-xs text-slate-500 mt-1">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner
        eyebrow="Get started"
        title="Need a feature you don't see?"
        subtitle="We add new capabilities every month — and our open API can fill any gap that's left. Tell us what you need and we'll show you in a 20-minute demo."
      />
    </>
  );
}
